import { z } from 'zod';
import rawMap from '../../data/visual-label-fragrance-map.v1.json';
import {
  fragranceSignalIdSchema,
  fragranceSignalOntology,
  type FragranceSignalId,
} from '../fragrance/signals';
import {
  getVisualStyleLabel,
  visualStyleFamilies,
  visualStyleVocabulary,
  type VisualStyleLabelId,
} from '../style-labels/vocabulary';
import type { StyleProfile } from '../style/schema';

const signalMappingSchema = z.object({
  signalId: fragranceSignalIdSchema,
  weight: z.number().min(0.1).max(0.55),
  confidence: z.enum(['low', 'medium']),
});

const visualSignalMapSchema = z.object({
  version: z.string().regex(/^\d+\.\d+\.\d+$/),
  visualVocabularyVersion: z.literal(visualStyleVocabulary.version),
  fragranceOntologyVersion: z.literal(fragranceSignalOntology.version),
  confidencePolicy: z.string().min(20),
  familyMappings: z.record(z.enum(visualStyleFamilies), z.array(signalMappingSchema).min(1).max(5)),
  labelOverrides: z.record(z.string(), z.array(signalMappingSchema).min(1).max(5)),
});

export const visualLabelFragranceMap = visualSignalMapSchema.parse(rawMap);
for (const labelId of Object.keys(visualLabelFragranceMap.labelOverrides)) {
  if (!getVisualStyleLabel(labelId)) throw new Error(`Unknown visual-label mapping override: ${labelId}`);
}
for (const label of visualStyleVocabulary.labels) {
  if (
    !visualLabelFragranceMap.labelOverrides[label.id] &&
    !visualLabelFragranceMap.familyMappings[label.family]
  ) {
    throw new Error(`Visual label has no direct or inherited fragrance mapping: ${label.id}`);
  }
}

export interface WeightedFragranceSignal {
  signalId: FragranceSignalId;
  weight: number;
  sourceLabelId: VisualStyleLabelId;
  sourceTier: 'core' | 'supporting';
  mappingConfidence: 'low' | 'medium';
}

const mappingsFor = (labelId: VisualStyleLabelId) => {
  const label = getVisualStyleLabel(labelId)!;
  return (
    visualLabelFragranceMap.labelOverrides[labelId] ??
    visualLabelFragranceMap.familyMappings[label.family]
  );
};

export const fragranceSignalsFromStyle = (profile: StyleProfile): WeightedFragranceSignal[] => {
  const weighted: WeightedFragranceSignal[] = [];
  const add = (
    labelId: VisualStyleLabelId,
    sourceTier: WeightedFragranceSignal['sourceTier'],
    tierWeight: number,
  ) => {
    for (const mapping of mappingsFor(labelId)) {
      weighted.push({
        signalId: mapping.signalId,
        weight: mapping.weight * tierWeight,
        sourceLabelId: labelId,
        sourceTier,
        mappingConfidence: mapping.confidence,
      });
    }
  };
  profile.calibratedCoreLabelIds.forEach((labelId) => add(labelId, 'core', 1));
  profile.calibratedSupportingLabelIds.forEach((labelId) => add(labelId, 'supporting', 0.4));
  return weighted;
};

